import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, FileText, Check, CheckCheck, Clock, MessageSquare, Paperclip, PlayCircle, Plus, Trash2, Pencil, AlertCircle, UserPlus, Calendar, Archive, Eye, EyeOff, RotateCcw } from 'lucide-react'
import Linkify from '../ui/Linkify'
import DangerDialog from '../ui/DangerDialog'
import MemberSearchDropdown from '../ui/MemberSearchDropdown'
import AttachmentLightbox from '../AttachmentLightbox'
import FilePreviewModal from '../ui/FilePreviewModal'
import CompletionKpiModal from '../CompletionKpiModal'
import CardActionsPanel from './CardActionsPanel'
import CardAttachmentsTab from './CardAttachmentsTab'
import CardChecklistTab from './CardChecklistTab'
import CardCommentsTab from './CardCommentsTab'
import CardTicketDetailsPanel from './CardTicketDetailsPanel'
import { getInitials, isImageAttachment } from './cardDetailUtils'
import { formatDue } from '../../lib/dueDate'

function autoSizeDescription(element) {
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
}

export default function CardDetailView(props) {
    const { canEditTask, detailModalRef, onDetailModalScroll, extensionMode } = props
    const overlayPointerRef = useRef(null)
    const commentsPanelRef = useRef(null)
    const {
        t, card, readOnly, onClose, updateError, isOverdue, effectiveIsCompleted, isProcedureCard, labels, editingTitle, title, setTitle, handleSaveTitle, setEditingTitle, cardDescription, hasDescriptionDraft, editingDesc, desc, handleSaveDesc, handleStartEditingDesc, handleChangeDesc, handleCancelDesc, handleUndoDesc, canEditDescription, canClaimPoolTask, handleClaimPoolTask, claimingPoolTask, poolClaimedByMe, canClaimProcedure, handleClaimProcedure, joiningProcedure, canLeaveProcedure, handleLeaveProcedure, activeTab, setActiveTab, totalItems, doneItems, attachments, comments, progressPct, procedureChecklists, manualChecklists, checklists, addingChecklist, setAddingChecklist, newChecklistName, setNewChecklistName, handleAddChecklist, addingChecklistLoading, procedureByChecklistId, user, canContribute, isAdmin, canManageCardMembers, canDeleteCard, members, companyMembers, editingItemId, editingItemText, setEditingItemId, setEditingItemText, updateItem, onRefreshCards, handleToggleItem, stepDoneQty, stepBusyId, qtyPopoverId, qtyDraft, setQtyDraft, setQtyPopoverId, handleStepToggle, handleStepQuantity, expandedSharedItem, setExpandedSharedItem, sharedItemSearch, setSharedItemSearch, sharedItemPicker, setSharedItemPicker, toggleShared, updateSharedMembers, deleteItem, newItemTexts, setNewItemTexts, handleAddItem, addingItemLoading, setConfirmDeleteChecklist, fileInputRef, handleFileUpload, uploadingFile, moveAttachment, setConfirmRemoveAttachment, procedureFiles, openLightbox, setPreviewFile, profile, commentReactions, commentImageInputRef, handleCommentImageUpload, commentText, setCommentText, submittingComment, handleAddComment, commentError, editingCommentId, editingCommentText, setEditingCommentId, setEditingCommentText, updateComment, removeComment, showAchievement, handleToggleCommentReaction, replyingTo, setReplyingTo, replyText, setReplyText, replySubmitting, replyImageInputRef, handleAddReply, renderFieldState, runFieldUpdate, configLocked, completionFields, effectiveTimerStartedAt, doneForCurrentUser, timeLogs, groupedTimeLogs, activeTimer, viewSession, loadingAverage, averageMinutes, availableMembers, addingMember, setAddingMember, handleAddMember, setConfirmRemoveMember, canWorkOnCard, cardActionPending, handleStartFromDetail, handleCompleteFromDetail, normalizedCompletionPayload, setSelectedKpiUserId, displayedKpiUserId, displayCompletionKpis, completionKpiDraft, setCompletionKpiDraft, setConfirmDelete, confirmDelete, onDelete, lightboxIndex, lightboxImages, setLightboxIndex, closeLightbox, previewFile, confirmRemoveMember, handleRemoveMember, confirmDeleteChecklist, removeChecklist, showCompletionModal, completionModalSaving, setShowCompletionModal, setCompletionModalSaving, setUpdateError, finalizeCardCompletion, isWatchingCard, watchLoading, watchSaving, handleToggleWatch
    } = props
    const visibleDetailTab = activeTab === 'history' ? 'checklist' : activeTab
    const cardFiles = attachments.filter((attachment) => !attachment.comment_id)

    const commentsPanel = (
        <div ref={commentsPanelRef} className="cd-sidebar-comments">
            <h4 className="cd-sidebar-comments-title">
                <MessageSquare size={14} />
                {t('card.tabs.comments')}
                {comments.length > 0 ? <span>{comments.length}</span> : null}
            </h4>
            <CardCommentsTab
                t={t}
                profile={profile}
                comments={comments}
                attachments={attachments}
                commentReactions={commentReactions}
                currentUserId={user?.id}
                commentImageInputRef={commentImageInputRef}
                handleCommentImageUpload={handleCommentImageUpload}
                commentText={commentText}
                setCommentText={setCommentText}
                companyMembers={companyMembers}
                submittingComment={submittingComment}
                uploadingFile={uploadingFile}
                handleAddComment={handleAddComment}
                commentError={commentError}
                editingCommentId={editingCommentId}
                editingCommentText={editingCommentText}
                setEditingCommentId={setEditingCommentId}
                setEditingCommentText={setEditingCommentText}
                updateComment={updateComment}
                removeComment={removeComment}
                showAchievement={showAchievement}
                openLightbox={openLightbox}
                setPreviewFile={setPreviewFile}
                handleToggleCommentReaction={handleToggleCommentReaction}
                replyingTo={replyingTo}
                setReplyingTo={setReplyingTo}
                replyText={replyText}
                setReplyText={setReplyText}
                replySubmitting={replySubmitting}
                replyImageInputRef={replyImageInputRef}
                handleAddReply={handleAddReply}
            />
        </div>
    )

    const handleOverlayPointerDown = (event) => {
        overlayPointerRef.current = {
            pointerId: event.pointerId,
            startedOutside: event.target === event.currentTarget,
            x: event.clientX,
            y: event.clientY,
        }
    }

    const handleOverlayPointerUp = (event) => {
        const press = overlayPointerRef.current
        overlayPointerRef.current = null
        if (!press || press.pointerId !== event.pointerId) return
        const distance = Math.hypot(event.clientX - press.x, event.clientY - press.y)
        if (press.startedOutside && event.target === event.currentTarget && distance < 6) onClose()
    }

    useEffect(() => {
        if (props.viewContext?.focus !== 'comments') return undefined
        const frame = requestAnimationFrame(() => {
            commentsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        return () => cancelAnimationFrame(frame)
    }, [card?.id, props.viewContext?.focus])

    return createPortal((
        <div
            className={`modal-overlay card-detail-overlay${extensionMode ? ' extension-card-detail-overlay' : ''}`}
            onPointerDown={handleOverlayPointerDown}
            onPointerUp={handleOverlayPointerUp}
            onPointerCancel={() => { overlayPointerRef.current = null }}
        >
            <div
                ref={detailModalRef}
                className={`card-detail-modal ${readOnly ? 'card-detail-readonly' : ''}`.trim()}
                onClick={e => e.stopPropagation()}
                onScroll={onDetailModalScroll}
            >
                <div className="card-detail-top-actions">
                    <button className="detail-panel-close" onClick={onClose}><X size={18} /></button>
                </div>

                {readOnly && (
                    <div className="card-detail-readonly-banner" role="status">
                        {t('card.readOnly.banner', { defaultValue: 'Mod spectator — doar citire. Vezi cardul, nu îl poți modifica.' })}
                    </div>
                )}

                {/* Title row — click anywhere on the title to rename */}
                <div className="card-detail-title-row">
                    {editingTitle ? (
                        <input
                            className="inline-input card-detail-title-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            onBlur={handleSaveTitle}
                            onKeyDown={e => {
                                if (e.key === 'Enter') handleSaveTitle()
                                if (e.key === 'Escape') { setTitle(card.title); setEditingTitle(false) }
                            }}
                            autoFocus
                        />
                    ) : (
                        <h2
                            className={`card-detail-title ${readOnly ? '' : 'card-detail-title-editable'} ${effectiveIsCompleted ? 'completed-text' : ''}`.trim()}
                            onClick={() => { if (!readOnly) setEditingTitle(true) }}
                            role={readOnly ? undefined : 'button'}
                            tabIndex={readOnly ? undefined : 0}
                            onKeyDown={e => { if (!readOnly && (e.key === 'Enter' || e.key === ' ')) setEditingTitle(true) }}
                            title={readOnly ? undefined : t('card.title.editTooltip')}
                        >
                            {card.title}
                            <Pencil size={13} className="card-detail-title-pencil" />
                        </h2>
                    )}
                </div>

                <div className="card-detail-body">
                    <div className="card-detail-main">

                        {updateError && (
                            <div className="date-error cd-error-block">
                                <AlertCircle size={14} /> {updateError}
                            </div>
                        )}
                        {/* Meta info row */}
                        {labels.length > 0 ? (
                            <div className="card-meta-row">
                                {labels.map(l => (
                                    <span key={l.id} className="card-label-tag" style={{ background: l.color }}>{l.name}</span>
                                ))}
                            </div>
                        ) : null}

                        {((!addingChecklist && canEditDescription) || (canManageCardMembers && availableMembers.length > 0) || (user && canContribute) || canWorkOnCard || canDeleteCard || members.length > 0 || card.due_date || addingMember || canClaimPoolTask || poolClaimedByMe || canClaimProcedure || canLeaveProcedure) ? (
                            <div className="cd-add-checklist-between-sections">
                                <div className="cd-quick-add-buttons">
                                    {!addingChecklist && canEditDescription ? (
                                        <button
                                            className="btn btn-secondary btn-sm cd-add-checklist-trigger"
                                            onClick={() => {
                                                setActiveTab('checklist')
                                                setAddingChecklist(true)
                                            }}
                                            title={t('card.checklist.addChecklist')}
                                            aria-label={t('card.checklist.addChecklist')}
                                        >
                                            <Plus size={13} />
                                            <span>{t('card.checklist.addChecklist')}</span>
                                        </button>
                                    ) : null}
                                    {canManageCardMembers && availableMembers.length > 0 && !addingMember ? (
                                        <button
                                            className="btn btn-secondary btn-sm cd-add-member-trigger"
                                            onClick={() => setAddingMember(true)}
                                            title={t('card.members.add')}
                                        >
                                            <UserPlus size={13} />
                                        </button>
                                    ) : null}
                                    {user && canContribute ? (
                                        <>
                                            <input
                                                type="file"
                                                multiple
                                                ref={fileInputRef}
                                                className="cd-file-input-hidden"
                                                onChange={handleFileUpload}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm cd-quick-attachment-trigger"
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploadingFile}
                                                title={t('card.attachments.upload')}
                                                aria-label={t('card.attachments.upload')}
                                            >
                                                <Paperclip size={14} />
                                            </button>
                                        </>
                                    ) : null}
                                    {user && !readOnly ? (
                                        <button
                                            type="button"
                                            className={`btn btn-secondary btn-sm cd-watch-trigger${isWatchingCard ? ' is-active' : ''}`}
                                            onClick={() => { void handleToggleWatch() }}
                                            disabled={watchLoading || watchSaving}
                                            title={isWatchingCard ? t('card.watch.disable') : t('card.watch.enable')}
                                        >
                                            {isWatchingCard ? <EyeOff size={14} /> : <Eye size={14} />}
                                            <span>{isWatchingCard ? t('card.watch.watching') : t('card.watch.watch')}</span>
                                        </button>
                                    ) : null}
                                    {canWorkOnCard && !doneForCurrentUser && !effectiveIsCompleted && !cardActionPending ? (
                                        effectiveTimerStartedAt ? (
                                            <button type="button" className="cd-quick-work-action is-running" disabled>
                                                <Clock size={14} />
                                                <span>{t('card.timer.statusRunning', { defaultValue: 'În proces' })}</span>
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                className="cd-quick-work-action is-start"
                                                onClick={handleStartFromDetail}
                                            >
                                                <PlayCircle size={14} />
                                                <span>{t('dashboard.actions.start')}</span>
                                            </button>
                                        )
                                    ) : null}
                                    {canWorkOnCard && !doneForCurrentUser && !cardActionPending ? (
                                        <button
                                            type="button"
                                            className="cd-quick-work-action is-complete"
                                            onClick={handleCompleteFromDetail}
                                        >
                                            <CheckCheck size={14} />
                                            <span>{t('dashboard.actions.complete')}</span>
                                        </button>
                                    ) : null}
                                    {canWorkOnCard && doneForCurrentUser && !isProcedureCard && !cardActionPending ? (
                                        <button
                                            type="button"
                                            className="cd-quick-work-action is-start"
                                            onClick={handleCompleteFromDetail}
                                            title={t('dashboard.done.reopenTitle')}
                                        >
                                            <RotateCcw size={14} />
                                            <span>{t('dashboard.done.reopenTitle')}</span>
                                        </button>
                                    ) : null}
                                    {canDeleteCard ? (
                                        <button
                                            type="button"
                                            className="cd-quick-work-action is-archive"
                                            onClick={() => setConfirmDelete(true)}
                                        >
                                            <Archive size={14} />
                                            <span>{t('card.deleteCard.action')}</span>
                                        </button>
                                    ) : null}
                                    {canEditTask ? (
                                        <label className="cd-quick-priority" title={t('card.sidebar.priorityLabel')}>
                                            <AlertCircle size={14} aria-hidden="true" />
                                            <span className="sr-only">{t('card.sidebar.priorityLabel')}</span>
                                            <select
                                                value={card.priority || 'medium'}
                                                onChange={(event) => { void runFieldUpdate('priority', { priority: event.target.value }) }}
                                                aria-label={t('card.sidebar.priorityLabel')}
                                            >
                                                <option value="lowest">{t('card.sidebar.priority.lowest')}</option>
                                                <option value="medium">{t('card.sidebar.priority.medium')}</option>
                                                <option value="highest">{t('card.sidebar.priority.highest')}</option>
                                            </select>
                                        </label>
                                    ) : null}
                                    {(canClaimPoolTask || poolClaimedByMe || canClaimProcedure || canLeaveProcedure) ? (
                                    <div className="cd-title-claim-actions">
                                        {canClaimPoolTask && (
                                            <button
                                                type="button"
                                                className="cd-claim-btn"
                                                onClick={handleClaimPoolTask}
                                                disabled={claimingPoolTask}
                                            >
                                                <PlayCircle size={13} /> {claimingPoolTask ? '...' : t('dashboard.actions.claim')}
                                            </button>
                                        )}
                                        {poolClaimedByMe && !canClaimPoolTask && (
                                            <span className="cd-claimed-pill">{t('dashboard.task.claimedByMe')}</span>
                                        )}
                                        {canClaimProcedure && (
                                            <button
                                                type="button"
                                                className="cd-claim-btn"
                                                onClick={handleClaimProcedure}
                                                disabled={joiningProcedure}
                                                title={t('card.procedure.claimHint')}
                                            >
                                                <Check size={13} /> {joiningProcedure ? '...' : t('card.procedure.claim')}
                                            </button>
                                        )}
                                        {canLeaveProcedure && (
                                            <button
                                                type="button"
                                                className="edit-btn"
                                                onClick={handleLeaveProcedure}
                                                disabled={joiningProcedure}
                                            >
                                                {joiningProcedure ? '...' : t('card.procedure.leave')}
                                            </button>
                                        )}
                                    </div>
                                    ) : null}
                                </div>
                                {addingMember ? (
                                    <div className="cd-quick-member-picker">
                                        <MemberSearchDropdown
                                            members={availableMembers}
                                            onSelect={handleAddMember}
                                            onClose={() => setAddingMember(false)}
                                        />
                                    </div>
                                ) : null}
                                {(members.length > 0 || card.due_date) ? (
                                    <div className="cd-assignment-meta-row">
                                        {members.length > 0 ? (
                                            <div className="cd-member-bubbles" aria-label={t('card.tabs.members')}>
                                                {members.map((member, index) => (
                                                    <button
                                                        key={member.id}
                                                        type="button"
                                                        className="cd-member-bubble"
                                                        style={{ background: `hsl(${(index * 71 + 200) % 360}, 50%, 40%)` }}
                                                        title={member.full_name}
                                                        aria-label={t('card.members.removeTooltip', { name: member.full_name })}
                                                        onClick={() => setConfirmRemoveMember(member)}
                                                        disabled={!canManageCardMembers}
                                                    >
                                                        {getInitials(member.full_name)}
                                                    </button>
                                                ))}
                                            </div>
                                        ) : null}
                                        {card.due_date ? (
                                            <div className={`card-meta-chip cd-assignment-due-date ${isOverdue ? 'overdue' : ''} ${effectiveIsCompleted ? 'done-chip' : ''}`}>
                                                <Calendar size={13} />
                                                {formatDue(card.due_date, 'dd MMM yyyy')}
                                                {isOverdue && !effectiveIsCompleted ? <span className="overdue-badge">{t('card.meta.overdue')}</span> : null}
                                                {effectiveIsCompleted ? <span className="done-badge">{t('card.meta.done')}</span> : null}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        ) : null}

                        {/* Description */}
                        <div className="card-detail-section">
                            <div className="card-detail-section-title">
                                <FileText size={16} /> {t('card.description.heading')}
                                <div className="cd-section-actions">
                                    {hasDescriptionDraft && !editingDesc && (
                                        <button className="cd-unsaved-description-btn" onClick={handleStartEditingDesc}>{t('card.description.unsavedChanges')}</button>
                                    )}
                                    {canEditDescription && !editingDesc && (
                                        <button className="edit-btn" onClick={handleStartEditingDesc}>{t('card.description.edit')}</button>
                                    )}
                                </div>
                            </div>
                            {editingDesc ? (
                                <div>
                                    <textarea
                                        ref={autoSizeDescription}
                                        className="card-detail-textarea"
                                        value={desc}
                                        onChange={e => {
                                            handleChangeDesc(e.target.value)
                                            autoSizeDescription(e.currentTarget)
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Escape') handleCancelDesc()
                                        }}
                                        placeholder={t('card.description.placeholder')}
                                        rows={4}
                                        autoFocus
                                    />
                                    <div className="cd-inline-actions">
                                        <button className="btn btn-primary btn-sm" onClick={handleSaveDesc}>{t('common.save')}</button>
                                        <button className="btn btn-secondary btn-sm" onClick={handleUndoDesc}>Undo</button>
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`card-detail-description ${canEditDescription ? 'clickable' : ''}`}
                                    onClick={() => canEditDescription && handleStartEditingDesc()}
                                >
                                    {cardDescription ? <Linkify text={cardDescription} /> : <span className="placeholder-text">{canEditDescription ? t('card.description.empty.admin') : t('card.description.empty.viewer')}</span>}
                                </div>
                            )}
                        </div>

                        {(cardFiles.length > 0 || procedureFiles.length > 0) ? (
                            <div className="card-detail-section cd-inline-attachments">
                                <CardAttachmentsTab
                                    t={t}
                                    user={user}
                                    canContribute={canContribute}
                                    attachments={cardFiles}
                                    moveAttachment={moveAttachment}
                                    onRemoveAttachmentRequest={setConfirmRemoveAttachment}
                                    procedureFiles={procedureFiles}
                                    openLightbox={openLightbox}
                                    setPreviewFile={setPreviewFile}
                                    showUploader={false}
                                    showEmpty={false}
                                />
                            </div>
                        ) : null}

                        {/* CHECKLIST TAB */}
                        {visibleDetailTab === 'checklist' && (
                            <CardChecklistTab
                                t={t}
                                totalItems={totalItems}
                                doneItems={doneItems}
                                progressPct={progressPct}
                                procedureChecklists={procedureChecklists}
                                manualChecklists={manualChecklists}
                                checklists={checklists}
                                addingChecklist={addingChecklist}
                                setAddingChecklist={setAddingChecklist}
                                newChecklistName={newChecklistName}
                                setNewChecklistName={setNewChecklistName}
                                handleAddChecklist={handleAddChecklist}
                                addingChecklistLoading={addingChecklistLoading}
                                procedureByChecklistId={procedureByChecklistId}
                                userId={user?.id}
                                readOnly={readOnly}
                                canCreate={canEditTask}
                                canContribute={canContribute}
                                members={members}
                                companyMembers={companyMembers}
                                editingItemId={editingItemId}
                                editingItemText={editingItemText}
                                setEditingItemId={setEditingItemId}
                                setEditingItemText={setEditingItemText}
                                updateItem={updateItem}
                                onRefreshCards={onRefreshCards}
                                handleToggleItem={handleToggleItem}
                                stepDoneQty={stepDoneQty}
                                stepBusyId={stepBusyId}
                                qtyPopoverId={qtyPopoverId}
                                qtyDraft={qtyDraft}
                                setQtyDraft={setQtyDraft}
                                setQtyPopoverId={setQtyPopoverId}
                                handleStepToggle={handleStepToggle}
                                handleStepQuantity={handleStepQuantity}
                                expandedSharedItem={expandedSharedItem}
                                setExpandedSharedItem={setExpandedSharedItem}
                                sharedItemSearch={sharedItemSearch}
                                setSharedItemSearch={setSharedItemSearch}
                                sharedItemPicker={sharedItemPicker}
                                setSharedItemPicker={setSharedItemPicker}
                                toggleShared={toggleShared}
                                updateSharedMembers={updateSharedMembers}
                                deleteItem={deleteItem}
                                newItemTexts={newItemTexts}
                                setNewItemTexts={setNewItemTexts}
                                handleAddItem={handleAddItem}
                                addingItemLoading={addingItemLoading}
                                setConfirmDeleteChecklist={setConfirmDeleteChecklist}
                            />
                        )}

                    </div>

                    {/* Right sidebar actions */}
                    <div className="card-detail-sidebar">
                        <CardTicketDetailsPanel
                            t={t}
                            card={card}
                            companyMembers={companyMembers}
                            renderFieldState={renderFieldState}
                            runFieldUpdate={runFieldUpdate}
                            configLocked={configLocked}
                            completionFields={completionFields}
                            readOnly={readOnly}
                            effectiveTimerStartedAt={effectiveTimerStartedAt}
                            doneForCurrentUser={doneForCurrentUser}
                            timeLogs={timeLogs}
                            groupedTimeLogs={groupedTimeLogs}
                            activeTimer={activeTimer}
                            viewSession={viewSession}
                            effectiveIsCompleted={effectiveIsCompleted}
                            userId={user?.id}
                            loadingAverage={loadingAverage}
                            averageMinutes={averageMinutes}
                            afterPoints={commentsPanel}
                        />

                        <CardActionsPanel
                                t={t}
                                completionFields={completionFields}
                                normalizedCompletionPayload={normalizedCompletionPayload}
                                members={members}
                                onSelectKpiUser={setSelectedKpiUserId}
                                displayedKpiUserId={displayedKpiUserId}
                                currentUserId={user?.id}
                                displayCompletionKpis={displayCompletionKpis}
                                canEditKpis={canEditTask && !effectiveIsCompleted}
                                onChangeKpiValue={(fieldKey, value) => {
                                    setCompletionKpiDraft((current) => ({ ...current, [fieldKey]: value }))
                                }}
                                isAdmin={isAdmin}
                                cardId={card.id}
                                userId={user?.id}
                            />
                    </div>
                </div>
            </div>
            <DangerDialog
                open={confirmDelete}
                title={t('card.deleteCard.title', { title: card.title })}
                description={t('card.deleteCard.description')}
                confirmLabel={t('card.deleteCard.confirm')}
                onClose={() => setConfirmDelete(false)}
                onConfirm={async () => {
                    await onDelete(card.id)
                    setConfirmDelete(false)
                    onClose()
                }}
            />
            {lightboxIndex !== null && (() => {
                const imgs = lightboxImages || attachments.filter(isImageAttachment)
                if (!imgs.length || lightboxIndex < 0 || lightboxIndex >= imgs.length) return null
                return (
                    <AttachmentLightbox
                        images={imgs}
                        index={lightboxIndex}
                        currentUserId={user?.id}
                        onNavigate={setLightboxIndex}
                        onClose={closeLightbox}
                    />
                )
            })()}
            {previewFile && (
                <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
            )}
            <DangerDialog
                open={Boolean(confirmRemoveMember)}
                title={t('card.removeMember.title', { name: confirmRemoveMember?.full_name || confirmRemoveMember?.email || '' })}
                description={t('card.removeMember.description')}
                confirmLabel={t('card.removeMember.confirm')}
                onClose={() => setConfirmRemoveMember(null)}
                onConfirm={() => handleRemoveMember(confirmRemoveMember.id)}
            />
            <DangerDialog
                open={Boolean(confirmDeleteChecklist)}
                title={t('card.deleteChecklist.title', { name: confirmDeleteChecklist?.name || '' })}
                description={t('card.deleteChecklist.description')}
                confirmLabel={t('card.deleteChecklist.confirm')}
                onClose={() => setConfirmDeleteChecklist(null)}
                onConfirm={async () => {
                    const cl = confirmDeleteChecklist
                    setConfirmDeleteChecklist(null)
                    await removeChecklist(cl.id)
                    onRefreshCards()
                }}
            />
            {showCompletionModal ? (
                <CompletionKpiModal
                    fields={completionFields}
                    initialValues={completionKpiDraft}
                    saving={completionModalSaving}
                    onClose={() => {
                        if (completionModalSaving) return
                        setShowCompletionModal(false)
                    }}
                    onSubmit={async (values) => {
                        setCompletionKpiDraft(values)
                        setCompletionModalSaving(true)
                        setUpdateError('')
                        try {
                            const result = await finalizeCardCompletion({ completionValues: values })
                            if (result?.success !== false) {
                                setShowCompletionModal(false)
                            }
                        } finally {
                            setCompletionModalSaving(false)
                        }
                    }}
                />
            ) : null}
        </div>
    ), document.body)
}
